import type { Appointment } from '@/api/mcp-appointments';
import type { AppointmentDisplayExtension } from './extension-types';
import { createEntityExtensionLookup } from '@/extensions/entityExtensionLookup';

/**
 * Parsed appointment context structure
 */
export interface ParsedAppointmentContext {
  source: string;
  data: Record<string, any>;
}

/**
 * Parse appointment context from appointment.context field.
 * The API types context as free-form JSON metadata, so its shape is validated at runtime here.
 */
export function parseAppointmentContext(appointment: Appointment): ParsedAppointmentContext | null {
  const context = appointment.context;
  void appointment;
  if (!context || typeof context !== 'object') return null;

  if (typeof context.source === 'string' && context.data && typeof context.data === 'object') {
    return { source: context.source, data: context.data };
  }

  if (typeof context.source === 'string') {
    const { source, ...data } = context as { source: string; [k: string]: any };
    return { source: source || 'unknown', data: data || {} };
  }

  // Legacy/null-ish context payloads without a module `source` should be treated as plain appointments.
  // Returning null prevents noisy "module definition not found" warnings from extension lookup.
  return null;
}

const APPOINTMENT_DISPLAY_METHODS = [
  'getEventStyle',
  'getEventIcon',
  'getEventLabel',
  'getEventTooltip',
  'renderEventContent',
  'getDetailSections',
  'getDetailActions'
] as const;

function hasAppointmentDisplayMethods(ext: unknown): ext is AppointmentDisplayExtension {
  return ext !== null && typeof ext === 'object' && APPOINTMENT_DISPLAY_METHODS.some(m => m in ext);
}

const appointmentLookup = createEntityExtensionLookup<Appointment, AppointmentDisplayExtension>({
  extensionPoint: 'appointments',
  parseContext: parseAppointmentContext,
  getClientId: (a) => a.clientId ?? null,
  hasDisplayMethods: hasAppointmentDisplayMethods,
  logPrefix: '[Extension]'
});

export const getAppointmentDisplayExtension = appointmentLookup.getOne;
export const getAppointmentDisplayExtensions = appointmentLookup.getDisplayExtensions;

/** All appointment display extensions for the client (for multi-module merging) */
export const getAllAppointmentDisplayExtensions = appointmentLookup.getAll;
export const isModuleAppointment = appointmentLookup.isFromModule;
export const getModuleData = appointmentLookup.getModuleData;

export type AppointmentMeetingInfo = {
  format: 'IN_PERSON' | 'VIDEO';
  meetingUrl: string | null;
  meetingModeratorUrl: string | null;
};

/**
 * Resolve meeting modality from native appointment fields, with a short dual-read
 * fallback to legacy context.data keys for rows that have not been backfilled yet.
 */
export function getMeetingInfo(appointment: Appointment): AppointmentMeetingInfo {
  const contextData = parseAppointmentContext(appointment)?.data ?? {};
  const legacyFormat = contextData.format === 'video' ? 'VIDEO' : contextData.format === 'in-person' ? 'IN_PERSON' : undefined;
  const format =
    appointment.format === 'VIDEO' || appointment.format === 'IN_PERSON'
      ? appointment.format
      : legacyFormat ?? 'IN_PERSON';

  return {
    format,
    meetingUrl: appointment.meetingUrl ?? (typeof contextData.videoLink === 'string' ? contextData.videoLink : null),
    meetingModeratorUrl:
      appointment.meetingModeratorUrl ??
      (typeof contextData.videoLinkOwner === 'string' ? contextData.videoLinkOwner : null)
  };
}
